import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Activity, MapPin, Calendar, ImageIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/lib/routes';

interface RecentRun {
  id: string;
  title?: string;
  ai_generated_title?: string;
  distance_miles?: number;
  equipment_type: string;
  start_time?: string;
  created_at: string;
  hero_photo_url?: string;
  run_segments?: any[];
}

interface ProfileActivityProps {
  recentRuns?: RecentRun[];
  isLoading?: boolean;
  onSeeRuns?: () => void;
}

const equipmentLabels: Record<string, string> = {
  jeep: 'Jeep',
  truck: 'Truck',
  sxs: 'SxS',
  atv: 'ATV',
  dirt_bike: 'Dirt Bike',
  adv_bike: 'Adventure Bike',
  other: 'Other',
};

export const ProfileActivity = ({ recentRuns, isLoading, onSeeRuns }: ProfileActivityProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Runs
          </CardTitle>
          {onSeeRuns && (
            <Button variant="outline" size="sm" onClick={onSeeRuns}>
              See Runs
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-4 min-w-max">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-40 w-64 shrink-0" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!recentRuns || recentRuns.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Runs
          </CardTitle>
          {onSeeRuns && (
            <Button variant="outline" size="sm" onClick={onSeeRuns}>
              See Runs
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
            <p className="text-muted-foreground">No recent activity</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Recent Runs
        </CardTitle>
        {onSeeRuns && (
          <Button variant="outline" size="sm" onClick={onSeeRuns}>
            See Runs
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto pb-2 -mx-2 px-2">
          <div className="flex gap-4 min-w-max">
            {recentRuns.map((run) => (
              <Link
                key={run.id}
                to={ROUTES.run(run.id)}
                className="block w-64 shrink-0 rounded-lg border bg-card hover:bg-accent/50 transition-colors overflow-hidden group"
              >
                {/* Hero photo or placeholder */}
                <div className="h-24 bg-muted relative overflow-hidden">
                  {run.hero_photo_url ? (
                    <img 
                      src={run.hero_photo_url} 
                      alt=""
                      className="w-full h-full object-cover photo-signature group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                    </div>
                  )}
                  <Badge 
                    variant="secondary" 
                    className="absolute top-2 right-2 text-xs"
                  >
                    {equipmentLabels[run.equipment_type] || run.equipment_type}
                  </Badge>
                </div>

                {/* Run info */}
                <div className="p-3">
                  <h4 className="font-medium text-foreground truncate text-sm">
                    {run.title || run.ai_generated_title || 'Untitled Run'}
                  </h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    {run.distance_miles && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {run.distance_miles.toFixed(1)} mi
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDistanceToNow(new Date(run.start_time || run.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
